<script setup lang="ts">
import { computed, ref, watchEffect, watch } from 'vue'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { QuillEditor } from '@vueup/vue-quill'
import { useRoute, useRouter } from 'vue-router'
import PageContent from '@/components/page-content.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import PageTitle from '@/components/page-title.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import Card from '@/components/Card/card-container.vue'
import CardContent from '@/components/Card/card-content.vue'
import CardHeader from '@/components/Card/card-header.vue'
import useLoader from '@/composables/loader'
import useNotifications from '@/composables/notifications'
import { useCreditNoteDraftSeed } from '@/composables/billing/useCreditNoteDraftSeed'
import { useGetCreditNote, useCreateCreditNoteLines } from '@/composables/billing/credit-notes'
import { faFloppyDisk, faXmark, faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import CreditNoteSkeleton from '@/features/credit-notes/components/CreditNoteSkeleton.vue'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import CreditNoteSkeletonCustomerDetails from '@/features/credit-notes/components/CreditNoteSkeletonCustomerDetails.vue'
import CreditNoteSkeletonBillTotal from '@/features/credit-notes/components/CreditNoteBillTotal.vue'
import CreditNoteSkeletonDetails from '@/features/credit-notes/components/CreditNoteSkeletonDetails.vue'
import CreditNoteLines from '@/features/credit-notes/components/CreditNoteLines.vue'
import { CreditNotesRouteNames } from '@/features/credit-notes/constants'
import { calcCreditNoteTotals } from '@/features/credit-notes/utils/totals'
import { createDraftFromSeed } from '@/features/credit-notes/store'
import type { CreditNoteCharge } from '@/api/billing/credit-note-schemas'

defineOptions({ name: 'CreateCreditNote' })

const route = useRoute()
const router = useRouter()
const { addSuccess, addError } = useNotifications()
const { wrap } = useLoader()
const assetFromQuery = computed(() => String(route.query.assetCode || '').trim())
const {
  seed,
  isLoading: seedLoading,
  isError: seedIsError
} = useCreditNoteDraftSeed(assetFromQuery)
const creatingDraft = ref(false)
const draftId = ref(0)

watchEffect(async () => {
  if (!assetFromQuery.value) return
  if (!seed.value) return
  if (creatingDraft.value) return
  if (draftId.value > 0) return

  creatingDraft.value = true
  try {
    const draft = await createDraftFromSeed(seed.value)
    draftId.value = draft.id ?? 0
  } catch {
    addError({ title: 'Unable to create draft credit note' })
  } finally {
    creatingDraft.value = false
  }
})

const noteId = computed(() => draftId.value)
const {
  data: note,
  isLoading: creditNoteLoading,
  isError: creditNoteIsError
} = useGetCreditNote(noteId)
const { mutateAsync: createInBackend, isPending: isCreating } = useCreateCreditNoteLines(noteId)

type Line = { id?: number; description: string; kWh: number; tariffPerKWh: number }
const lines = ref<Line[]>([])
const vatPercentage = ref(0)
const notes = ref('')
const hasHydrated = ref(false)

function normaliseVat(value: unknown): number {
  if (typeof value !== 'number') return 0
  if (Number.isNaN(value)) return 0
  if (value <= 0 || value > 100) return 0
  return value
}

watch(
  note,
  n => {
    if (!n) return
    if (hasHydrated.value) return

    hasHydrated.value = true
    vatPercentage.value = normaliseVat(n.vatPercentage)
    notes.value = n.notes ?? ''

    const src = n.charges?.length ? n.charges : [{ description: '', kWh: 0, tariffPerKWh: 0 }]
    lines.value = src.map<Line>(l => ({
      id: l.id,
      description: l.description ?? '',
      kWh: l.kWh ?? 0,
      tariffPerKWh: l.tariffPerKWh ?? 0
    }))
  },
  { immediate: true }
)

const canDeleteLine = computed(() => lines.value.length > 1)
const hasUniqueDescriptions = computed(() => {
  const seen = new Set<string>()
  for (const l of lines.value) {
    const desc = (l.description ?? '').trim()
    if (!desc) continue
    const key = desc.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
  }
  return true
})

const isDraftReady = computed(() => draftId.value > 0 && !!note.value && hasHydrated.value)
const isFormValid = computed(() => {
  if (!lines.value.length) return false
  if (!hasUniqueDescriptions.value) return false
  return lines.value.every(l => (l.description ?? '').trim().length > 0)
})

const canSave = computed(() => isDraftReady.value && isFormValid.value)
const totals = computed(() => calcCreditNoteTotals(lines.value, vatPercentage.value))
const displayTotalCents = computed(() => totals.value.totalCents)

function goBackToDashboard() {
  router.push({ name: 'invoices.list' })
}

function addLine() {
  lines.value.push({ description: '', kWh: 0, tariffPerKWh: 0 })
}

async function removeLine(i: number) {
  await wrap(async () => {
    if (lines.value.length <= 1) return
    lines.value.splice(i, 1)
  })
}

function updateLine({ index, line }: { index: number; line: Line }) {
  lines.value[index] = line
}

async function doSave() {
  if (!canSave.value) return

  await wrap(async () => {
    const payload: CreditNoteCharge[] = lines.value.map(l => ({
      id: l.id,
      description: l.description,
      kWh: l.kWh,
      tariffPerKWh: l.tariffPerKWh
    }))

    await createInBackend({
      charges: payload,
      vatPercentage: vatPercentage.value,
      notes: notes.value
    })

    addSuccess({ title: 'Credit note created' })
    await router.replace({ name: CreditNotesRouteNames.edit, params: { id: draftId.value } })
  })
}

function doCancel() {
  router.push({ name: 'invoices.list' })
}
</script>

<template>
  <PageContent>
    <ContentLoader :loading="!note && (seedLoading || creatingDraft || creditNoteLoading)" />
    <HeroSplashError v-if="(seedIsError || creditNoteIsError) && !note" />

    <div v-else-if="note">
      <div class="mb-4">
        <PageTitle>
          {{ note.projectName }}
          <template #subtitle
            >(Project: {{ note.projectCode }} | Asset: {{ note.assetCode }})</template
          >
        </PageTitle>

        <div class="mt-2">
          <ButtonSecondary
            :icon="faArrowLeft"
            :class="['one-h-width', $style['back-to-dashboard-btn']]"
            @click="goBackToDashboard">
            Back to Dashboard
          </ButtonSecondary>
        </div>
      </div>

      <CreditNoteSkeleton title="Credit Note">
        <template #credit-note-details>
          <CreditNoteSkeletonDetails
            :credit-note-number="note.creditNoteNumber"
            :date="note.date"
            :date-format="note.creditNoteDateFormat" />
        </template>

        <template #bill-total>
          <CreditNoteSkeletonBillTotal :amount="displayTotalCents" label="Your Credit" />
        </template>

        <template #owner-details>
          <InvoiceSkeletonOwnerDetails
            :logo-url="note.owner.logoUrl ?? null"
            :name="note.owner.name"
            :address="note.owner.address"
            :vat-number="note.owner.vatNumber" />
        </template>

        <template #customer-details>
          <CreditNoteSkeletonCustomerDetails
            :name="note.customer.name"
            :address="[
              note.customer.addressLineOne ?? null,
              note.customer.addressLineTwo ?? null,
              note.customer.suburb ?? null,
              note.customer.city ?? null,
              note.customer.region ?? null,
              note.customer.country ?? null
            ]"
            :vat-number="note.customer.vatNumber ?? null" />
        </template>

        <template #line-items>
          <CreditNoteLines
            :lines="lines"
            :vat-percentage="vatPercentage"
            :can-delete="canDeleteLine"
            :readonly="false"
            @add="addLine"
            @remove="removeLine"
            @update:line="updateLine" />
        </template>

        <template #notes>
          <Card class="mt-4">
            <CardHeader uppercase>NOTES</CardHeader>
            <CardContent>
              <QuillEditor
                v-model:content="notes"
                contentType="html"
                theme="snow"
                placeholder="Please type notes here"
                style="min-height: 120px" />
            </CardContent>
          </Card>
        </template>
      </CreditNoteSkeleton>

      <div class="mt-4 d-flex flex-wrap align-items-center gap-3">
        <ButtonPrimary
          :icon="faFloppyDisk"
          class="color-temp-white"
          :disabled="!canSave || isCreating"
          @click="doSave">
          Save
        </ButtonPrimary>

        <ButtonSecondary
          :icon="faXmark"
          class="one-h-width"
          :disabled="isCreating"
          @click="doCancel">
          Cancel
        </ButtonSecondary>
      </div>
    </div>
  </PageContent>
</template>

<style module>
.back-to-dashboard-btn {
  min-width: 180px;
}
</style>
